export type Lender = {
  lenderId: number;
  lenderName: string;
};

export type LoanProduct = {
  loanProductId: number;
  lenderId: number;
  lenderName: string;
  productName: string;
  productType: string;
  productRank: number;
  loanDetailsJson: string;
  isDeleted: boolean;
  InterestFrom?: string;
  InterestTo?: string;
  LoanAmountMin?: string;
};

export type LoanProductForm = {
  lenderId: string;
  productName: string;
  productType: string;
  loanDetailsJson: string;
};

export type LoanDocumentationRow = {
  category: string;
  options: string[];
};

export type LoanDocumentationSelection = Record<string, Record<string, boolean>>;

export type PropertyLvrBand = {
  lvrLabel: string;
  residential1stFrom: string;
  residential1stTo: string;
  residential2ndFrom: string;
  residential2ndTo: string;
  commercialFrom: string;
  commercialTo: string;
  farmRuralFrom: string;
  farmRuralTo: string;
};

export type PropertyFeeCharge = {
  label: string;
  amount: string;
  percent: string;
};

export type PropertyDetails = {
  loanAmountMin: string;
  loanAmountMax: string;
  loanTermMonthsMin: string;
  loanTermMonthsMax: string;
  primaryPurpose: string[];
  repaymentType: string[];
  interestCapitalised: "" | "Yes" | "No";
  maximumExtensionAllowed: string;
  maximumExtensionMonths: string;
  valuation: string[];
  fundingSpeed: string[];
  smsfLoan: "" | "Yes" | "No";
  developmentLoan: "" | "Yes" | "No";
  incomeVerification: string[];
  atoDebt: string[];
  creditProfile: string[];
  creditScoreCompany: string;
  creditScoreDirector: string;
  securityPosition: string[];
  propertyTypeResidential: "" | "Yes" | "No";
  propertyTypeCommercial: "" | "Yes" | "No";
  propertyTypeRural: "" | "Yes" | "No";
  statesAccepted: string[];
  lvrInterestRates: PropertyLvrBand[];
  feesAndCharges: PropertyFeeCharge[];
  commissionFee: string;
};

export type AssetInterestRateRow = {
  label: string;
  from: string;
  to: string;
};

export type AssetFeeCharge = {
  label: string;
  amount: string;
  percent: string;
};

export type AssetSecuredDetails = {
  loanAmountMin: string;
  loanAmountMax: string;
  loanTermMonthsMin: string;
  loanTermMonthsMax: string;
  balloonPercentAllowed: string;
  minimumMonthlyTurnover: string;
  minimumTimeTrading: string[];
  primaryPurpose: string[];
  repaymentType: string[];
  fundingSpeed: string[];
  smsfLoan: "" | "Yes" | "No";
  developmentLoan: "" | "Yes" | "No";
  homeowner: "" | "Yes" | "No";
  equityRequired: string;
  electricVehicle?: string;
  incomeVerification: string;
  atoDebt: string[];
  creditProfile: string[];
  creditScoreCompany: string;
  creditScoreDirector: string;
  assetType: string[];
  assetSubClass: string[];
  industryExcluded: string[];
  feesAndCharges: AssetFeeCharge[];
  interestRates: AssetInterestRateRow[];
  commissionFee: string;
};

export type AssetUnsecuredDetails = {
  loanAmountMin: string;
  loanAmountMax: string;
  loanTermMonthsMin: string;
  loanTermMonthsMax: string;
  minimumMonthlyTurnover: string;
  minimumTimeTrading: string[];
  primaryPurpose: string[];
  fundingSpeed: string[];
  incomeVerification: string;
  atoDebt: string[];
  creditProfile: string[];
  creditScoreCompany: string;
  creditScoreDirector: string;
  industryExcluded: string[];
  feesAndCharges: AssetFeeCharge[];
  interestRates: AssetInterestRateRow[];
};

export const toggleOptionInArray = (items: string[], value: string, checked: boolean) => {
  if (checked) {
    return items.includes(value) ? items : [...items, value];
  }
  return items.filter((item) => item !== value);
};

export const formatIntegerInput = (value: string, maxLength = 9) => {
  return value.replace(/\D/g, "").slice(0, maxLength);
};

export const formatCurrencyWithCommasInput = (value: string, maxDigits = 12) => {
  const digits = value.replace(/\D/g, "").slice(0, maxDigits);
  if (!digits) {
    return "";
  }
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

export const formatTwoDecimalInput = (value: string, maxIntegerDigits = 2) => {
  let cleaned = value.replace(/[^\d.]/g, "");
  const firstDot = cleaned.indexOf(".");

  if (firstDot !== -1) {
    cleaned =
      cleaned.slice(0, firstDot + 1) +
      cleaned
        .slice(firstDot + 1)
        .replace(/\./g, "");
  }

  const [integerPartRaw = "", decimalPartRaw = ""] = cleaned.split(".");
  const integerPart = integerPartRaw.slice(0, maxIntegerDigits);
  const decimalPart = decimalPartRaw.slice(0, 2);

  if (cleaned.includes(".")) {
    return `${integerPart}.${decimalPart}`;
  }

  return integerPart;
};
export const finalizeTwoDecimalInput = (value: string, maxIntegerDigits = 2) => {
  const formatted = formatTwoDecimalInput(value, maxIntegerDigits);
  if (!formatted) {
    return "";
  }

  const [integerPart = "", decimalPart = ""] = formatted.split(".");
  if (!formatted.includes(".")) {
    return `${integerPart}.00`;
  }
  if (decimalPart.length === 0) {
    return `${integerPart}.00`;
  }
  if (decimalPart.length === 1) {
    return `${integerPart}.${decimalPart}0`;
  }

  return `${integerPart}.${decimalPart.slice(0, 2)}`;
};

export const productTypeOptions = [
  { value: "Property", label: "Secured - Property" },
  { value: "Asset - Secured", label: "Secured - Asset/Equipment" },
  { value: "Asset - Unsecured", label: "Unsecured - Cashflow loan" },
];

export const getProductTypeLabel = (value: string): string => {
  const option = productTypeOptions.find((o) => o.value === value);
  return option ? option.label : value;
};

export const commonYesNo = ["Yes", "No"];

export const commonIncomeVerificationOptions = [
  "BAS Statements",
  "Full Financials",
  "No Financials - Accountant Letter",
  "No Financials - No Accountant Letter",
].sort();

export const commonAtoDebtOptions = ["ATO Debt Accepted", "ATO Arrangement Accepted"];

export const commonCreditProfileOptions = [
  "Good Credit",
  "Late Payments",
  "Defaults Paid",
  "Defaults Unpaid",
  "Judgements"
];

export const commonRepaymentTypeOptions = ["Interest Only", "Principal & Interest"];
export const commonRepaymentFrequencyOptions = ["Daily", "Weekly", "Fortnightly", "Monthly"];
export const commonFundingSpeedOptions = ["24 - 48 hours", "1 week", "2 - 4 weeks"];
export const commonMinimumTimeTradingOptions = ["1 Day ABN", "Under 6 months", "6-12 months", "1-2 years", "2+ years"];

export const securedPrimaryPurposeOptions = [
  "Equipment Purchase",
  "Vehicle Purchase",
].sort();

export const unsecuredPrimaryPurposeOptions = [
  "Business Cashflow",
  "Equipment Purchase",
  "Vehicle Purchase",
].sort();

export const assetSecuredHomeownerOptions = ["Yes", "No"];
export const assetSecuredEquityRequiredOptions = ["None", "$100,000", "$150,000", "$200,000", "$250,000", "$300,000"];

export const assetSecuredIncomeVerificationOptions = [
  "Full Doc - Financials",
  "Low Doc - Accountant Letter",
  "Lite Doc - BAS & Bank Statements",
  "No Doc",
];

export const securedAssetTypeOptions = [
  "Motor Vehicle <4.5 Ton",
  "Heavy Vehicle >4.5 Ton",
  "Agricultural",
  "Yellow Goods",
  "Construction",
  "Other",
];

export const securedAssetSubClassMap: Record<string, string[]> = {
  "MV <4.5 Ton": ["Sedan", "Hatch", "SUV", "Ute", "Van", "Mini truck"],
  "HV >4.5 Ton": ["Prime mover", "Rigid truck", "Trailer", "Bus", "Crane"],
  "Agriculture": ["Tractor", "Harvester", "Sprayer", "Seeder", "Irrigation equipment"],
  "Yellow": ["Excavator", "Loader", "Grader", "Bulldozer", "Dump truck", "Backhoe"],
  "Construction": ["Bobcat", "Forklift", "Scissor lift", "Boom lift", "Roller", "Telehandler"],
  "Other": ["Air conditioning units", "Solar equipment", "POS systems", "Computers", "Fuel tanks", "Amusement machines", "Signage" ],
  "Other - Not Known": ["Plant and equipment", "Office equipment", "Medical equipment", "Marine equipment", "Work tools"]
};

export const securedIndustryExcludedOptions = [
  "Adult Industry", "Agriculture and Farming", "Alcohol", "Aviation", "Automotive", "Beauty", "Builders", "Cannabis", "Coal Mining", "Construction", "Consulting", "Cryptocurrency", "Debt Collection",
  "Defence", "Drilling and Exploration", "Education and Training", "Energy Production", "Financial Services", "Fishing", "Forestry and Logging", "Freight and Shipping", "Gambling", "Hairdressing", "Healthcare", "Hospitality", "Insurance", "Legal Services",
  "Manufacturing", "Marketing", "Media", "Mining", "Oil and Gas", "Passenger Transport", "Pawnbrokers", "Petrol", "Pharmaceutical", "Real Estate", "Property Development", "Recycling", "Renewable Energy",
  "Restaurants", "Retail and E commerce", "Rideshare Uber / Taxi", "Security Services", "Tattoo Studios", "Taxi / Chauffer", "Technology", "Telco", "Tobacco", "Transport", "Waste Management"
].sort();

export const securedInterestRateLabels = ["Interest Rate"];

export const unsecuredInterestRateLabels = ["Interest Rate"];

export const defaultFeeLabels = [
  "Assessment Fee",
  "Establishment Fee",
  "Legal Fee",
  "Line Fee",
  "Valuation Fee",
];

export const propertyPrimaryPurposeOptions = [
  "Business Cashflow",
  "Construction Loan",
  "Property Refinance",
  "Property Purchase",
].sort();

export const propertyRepaymentTypeOptions = ["Interest Only", "Principal & Interest", "Interest Capitalised"];
export const propertyRepaymentFrequencyOptions = commonRepaymentFrequencyOptions;
export const propertyValuationOptions = ["No Valuation", "Desktop Valuation", "Full Valuation"];
export const propertyFundingSpeedOptions = commonFundingSpeedOptions;

export const propertyIncomeVerificationOptions = [
  "Full Doc - Financials",
  "Low Doc - Accountant Letter",
  "Lite Doc - BAS & Bank Statements",
  "No Doc",
];

export const propertyAtoDebtOptions = commonAtoDebtOptions;

export const propertyCreditProfileOptions = commonCreditProfileOptions;

export const propertySecurityPositionOptions = ["1st Mortgage", "2nd Mortgage"];


const createDefaultFeeRows = (): AssetFeeCharge[] =>
  defaultFeeLabels.map((label) => ({
    label,
    amount: "",
    percent: "",
  }));

const createInterestRows = (labels: string[]): AssetInterestRateRow[] =>
  labels.map((label) => ({
    label,
    from: "",
    to: "",
  }));

export const createDefaultPropertyDetails = (): PropertyDetails => ({
  loanAmountMin: "",
  loanAmountMax: "",
  loanTermMonthsMin: "",
  loanTermMonthsMax: "",
  primaryPurpose: [],
  repaymentType: [],
  interestCapitalised: "",
  maximumExtensionAllowed: "",
  maximumExtensionMonths: "",
  valuation: [],
  fundingSpeed: [],
  smsfLoan: "",
  developmentLoan: "",
  incomeVerification: [],
  atoDebt: [],
  creditProfile: [],
  creditScoreCompany: "",
  creditScoreDirector: "",
  securityPosition: [],
  propertyTypeResidential: "",
  propertyTypeCommercial: "",
  propertyTypeRural: "",
  statesAccepted: [],
  lvrInterestRates: [
    {
      lvrLabel: "Up to 65%",
      residential1stFrom: "",
      residential1stTo: "",
      residential2ndFrom: "",
      residential2ndTo: "",
      commercialFrom: "",
      commercialTo: "",
      farmRuralFrom: "",
      farmRuralTo: "",
    },
    {
      lvrLabel: "66% to 75%",
      residential1stFrom: "",
      residential1stTo: "",
      residential2ndFrom: "",
      residential2ndTo: "",
      commercialFrom: "",
      commercialTo: "",
      farmRuralFrom: "",
      farmRuralTo: "",
    },
    {
      lvrLabel: "76% to 80%",
      residential1stFrom: "",
      residential1stTo: "",
      residential2ndFrom: "",
      residential2ndTo: "",
      commercialFrom: "",
      commercialTo: "",
      farmRuralFrom: "",
      farmRuralTo: "",
    },
  ],
  feesAndCharges: createDefaultFeeRows(),
  commissionFee: "",
});

export const createDefaultAssetSecuredDetails = (): AssetSecuredDetails => ({
  loanAmountMin: "",
  loanAmountMax: "",
  loanTermMonthsMin: "",
  loanTermMonthsMax: "",
  balloonPercentAllowed: "",
  minimumMonthlyTurnover: "",
  minimumTimeTrading: [],
  primaryPurpose: [],
  repaymentType: [],
  fundingSpeed: [],
  smsfLoan: "",
  developmentLoan: "",
  homeowner: "",
  equityRequired: "",
  electricVehicle: "",
  incomeVerification: "",
  atoDebt: [],
  creditProfile: [],
  creditScoreCompany: "",
  creditScoreDirector: "",
  assetType: [],
  assetSubClass: [],
  industryExcluded: [],
  feesAndCharges: createDefaultFeeRows(),
  interestRates: createInterestRows(securedInterestRateLabels),
  commissionFee: "",
});

export const createDefaultAssetUnsecuredDetails = (): AssetUnsecuredDetails => ({
  loanAmountMin: "",
  loanAmountMax: "",
  loanTermMonthsMin: "",
  loanTermMonthsMax: "",
  minimumMonthlyTurnover: "",
  minimumTimeTrading: [],
  primaryPurpose: [],
  fundingSpeed: [],
  incomeVerification: "",
  atoDebt: [],
  creditProfile: [],
  creditScoreCompany: "",
  creditScoreDirector: "",
  industryExcluded: [],
  feesAndCharges: createDefaultFeeRows(),
  interestRates: createInterestRows(unsecuredInterestRateLabels),
});

export const loanDocumentationRows: LoanDocumentationRow[] = [
  { category: "Identification", options: ["Drivers Licence", "Passport", "Medicare Card"] },
  {
    category: "Income Verification - PAYG",
    options: ["2 recent pay slips", "ATO Income Statement", "Employment Letter", "Last 2 years Tax returns"],
  },
  {
    category: "Income Verification - SE",
    options: ["Last 2 years Tax returns", "Last 2 years Financials", "Last 3 months BAS Statements", "Accountant Letter"],
  },
  { category: "Bank Statements", options: ["Bank Statements last 3 months", "Bank Statements last 6 months"] },
  {
    category: "Liabilities",
    options: ["Last 3 months Personal Loans", "Last 3 months Car Loans", "Last 3 months Credit Card", "HECS / HELP balance"],
  },
  { category: "Savings", options: ["Last 3 months savings", "Gift Letter for Deposit"] },
  { category: "Property Purchase", options: ["Copy of Contract of Sale", "Copy of Deposit Receipt"] },
  { category: "Renting", options: ["Last Rental statement", "Copy of Lease Agreement"] },
  {
    category: "Home - PPOR",
    options: ["Copy of Rates Notice", "Last 6 months Loan statement", "Copy of Building Insurance"],
  },
  {
    category: "Investment Property",
    options: ["Copy of Rates Notice", "Last 6 months Loan statements", "Rental Income Statements", "Rental Agreement"],
  },
  {
    category: "Construction New Property",
    options: ["Copy of Build Contract", "Plans & Specifications", "Progress Payment Schedule", "Building Permit"],
  },
  {
    category: "SMSF",
    options: ["Executed Bare Trust & Trust Deed", "Last 2 years Tax Returns", "Last 2 years Financials", "Current Bank Statement"],
  },
];

export const createDefaultLoanDocumentationSelection = (): LoanDocumentationSelection => {
  const selection: LoanDocumentationSelection = {};

  loanDocumentationRows.forEach((row) => {
    selection[row.category] = {};
    row.options.forEach((option) => {
      selection[row.category][option] = false;
    });
  });

  return selection;
};

export const getSelectedDocumentationCount = (selection: LoanDocumentationSelection): number => {
  return Object.values(selection).reduce((count, row) => count + Object.values(row).filter(Boolean).length, 0);
};

export const buildLoanDetailsJsonFromSelection = (
  selection: LoanDocumentationSelection,
  productType?: string,
  propertyDetails?: PropertyDetails,
  assetSecuredDetails?: AssetSecuredDetails,
  assetUnsecuredDetails?: AssetUnsecuredDetails
): string => {
  const payload: any = {
    documentationRequired: loanDocumentationRows.map((row) => ({
      category: row.category,
      items: row.options.filter((option) => selection[row.category]?.[option]),
    })),
    productTypeConfig: { type: productType || "" },
  };

  if (productType === "Property") {
    payload.productTypeConfig.property = propertyDetails || createDefaultPropertyDetails();
  }

  if (productType === "Asset - Secured") {
    payload.productTypeConfig.assetSecured = assetSecuredDetails || createDefaultAssetSecuredDetails();
  }

  if (productType === "Asset - Unsecured") {
    payload.productTypeConfig.assetUnsecured = assetUnsecuredDetails || createDefaultAssetUnsecuredDetails();
  }

  return JSON.stringify(payload);
};

export const parseLoanDetailsJsonToSelection = (loanDetailsJson: string): LoanDocumentationSelection => {
  const selection = createDefaultLoanDocumentationSelection();

  if (!loanDetailsJson?.trim()) {
    return selection;
  }

  try {
    const parsed = JSON.parse(loanDetailsJson);

    if (Array.isArray(parsed?.documentationRequired)) {
      parsed.documentationRequired.forEach((entry: any) => {
        const category = typeof entry?.category === "string" ? entry.category : "";
        const items = Array.isArray(entry?.items) ? entry.items : [];

        if (!selection[category]) {
          return;
        }

        items.forEach((item: any) => {
          const option = String(item || "").trim();
          if (option && Object.prototype.hasOwnProperty.call(selection[category], option)) {
            selection[category][option] = true;
          }
        });
      });
    }
  } catch {
    return selection;
  }

  return selection;
};

const safeMerge = <T extends object>(fallback: T, value: any): T => {
  if (!value || typeof value !== "object") {
    return fallback;
  }
  return { ...fallback, ...value };
};

export const parsePropertyDetailsFromLoanDetailsJson = (loanDetailsJson: string): PropertyDetails => {
  const fallback = createDefaultPropertyDetails();

  if (!loanDetailsJson?.trim()) {
    return fallback;
  }

  try {
    const parsed = JSON.parse(loanDetailsJson);
    const property = parsed?.productTypeConfig?.property;

    const propertyTypeResidential = property?.propertyTypeResidential || (Array.isArray(property?.residentialPropertyTypes) && property.residentialPropertyTypes.length > 0 ? "Yes" : "");
    const propertyTypeCommercial = property?.propertyTypeCommercial || (Array.isArray(property?.commercialPropertyTypes) && property.commercialPropertyTypes.length > 0 ? "Yes" : "");

    const merged = safeMerge(fallback, property);
    merged.propertyTypeResidential = propertyTypeResidential || merged.propertyTypeResidential;
    merged.propertyTypeCommercial = propertyTypeCommercial || merged.propertyTypeCommercial;

    return {
      ...merged,
      lvrInterestRates: fallback.lvrInterestRates.map((defaultBand) => {
        const savedBand = Array.isArray(property?.lvrInterestRates)
          ? property.lvrInterestRates.find((r: any) => r.lvrLabel === defaultBand.lvrLabel)
          : null;
        return savedBand ? { ...defaultBand, ...savedBand } : defaultBand;
      }),
      feesAndCharges: fallback.feesAndCharges.map((defaultFee) => {
        const savedFee = Array.isArray(property?.feesAndCharges)
          ? property.feesAndCharges.find((r: any) => r.label === defaultFee.label)
          : null;
        return savedFee ? { ...defaultFee, ...savedFee } : defaultFee;
      }),
    };
  } catch {
    return fallback;
  }
};

export const parseAssetSecuredDetailsFromLoanDetailsJson = (loanDetailsJson: string): AssetSecuredDetails => {
  const fallback = createDefaultAssetSecuredDetails();

  if (!loanDetailsJson?.trim()) {
    return fallback;
  }

  try {
    const parsed = JSON.parse(loanDetailsJson);
    const value = parsed?.productTypeConfig?.assetSecured;
    const merged = safeMerge(fallback, value);
    
    const assetType = Array.isArray(merged.assetType) ? merged.assetType : (typeof merged.assetType === "string" && merged.assetType ? [merged.assetType] : []);
    const assetSubClass = Array.isArray(merged.assetSubClass) ? merged.assetSubClass : (typeof merged.assetSubClass === "string" && merged.assetSubClass ? [merged.assetSubClass] : []);
    const incomeVerification = Array.isArray(merged.incomeVerification) ? (merged.incomeVerification[0] || "") : (typeof merged.incomeVerification === "string" ? merged.incomeVerification : "");

    return {
      ...merged,
      assetType,
      incomeVerification,
      feesAndCharges: fallback.feesAndCharges.map((defaultFee) => {
        const savedFee = Array.isArray(value?.feesAndCharges)
          ? value.feesAndCharges.find((r: any) => r.label === defaultFee.label)
          : null;
        return savedFee ? { ...defaultFee, ...savedFee } : defaultFee;
      }),
      interestRates: fallback.interestRates.map((defaultRate) => {
        const savedRate = Array.isArray(value?.interestRates)
          ? value.interestRates.find((r: any) => r.label === defaultRate.label)
          : null;
        return savedRate ? { ...defaultRate, ...savedRate } : defaultRate;
      }),
    };
  } catch {
    return fallback;
  }
};

export const parseAssetUnsecuredDetailsFromLoanDetailsJson = (loanDetailsJson: string): AssetUnsecuredDetails => {
  const fallback = createDefaultAssetUnsecuredDetails();

  if (!loanDetailsJson?.trim()) {
    return fallback;
  }

  try {
    const parsed = JSON.parse(loanDetailsJson);
    const value = parsed?.productTypeConfig?.assetUnsecured;
    const merged = safeMerge(fallback, value);
    
    const incomeVerification = Array.isArray(merged.incomeVerification) ? (merged.incomeVerification[0] || "") : (typeof merged.incomeVerification === "string" ? merged.incomeVerification : "");

    return {
      ...merged,
      incomeVerification,
      feesAndCharges: fallback.feesAndCharges.map((defaultFee) => {
        const savedFee = Array.isArray(value?.feesAndCharges)
          ? value.feesAndCharges.find((r: any) => r.label === defaultFee.label)
          : null;
        return savedFee ? { ...defaultFee, ...savedFee } : defaultFee;
      }),
      interestRates: fallback.interestRates.map((defaultRate) => {
        const savedRate = Array.isArray(value?.interestRates)
          ? value.interestRates.find((r: any) => r.label === defaultRate.label)
          : null;
        return savedRate ? { ...defaultRate, ...savedRate } : defaultRate;
      }),
    };
  } catch {
    return fallback;
  }
};

export const defaultLoanProductForm: LoanProductForm = {
  lenderId: "",
  productName: "",
  productType: "",
  loanDetailsJson: "",
};

const getNumber = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getString = (value: unknown): string => {
  return typeof value === "string" ? value : value === null || value === undefined ? "" : String(value);
};

export const normalizeLender = (item: any): Lender => {
  const lenderId = getNumber(item?.lenderId ?? item?.lenderID ?? item?.LENDER_ID ?? item?.id ?? item?.ID);
  const lenderName = getString(item?.lenderName ?? item?.LENDER_NAME ?? item?.name ?? item?.NAME).trim();
  return { lenderId, lenderName };
};

export const normalizeLoanProduct = (item: any): LoanProduct => {
  const getCleanString = (val: any): string => {
    if (val === undefined || val === null) return "";
    if (typeof val === "object") return "";
    const s = String(val).trim();
    if (s.toLowerCase() === "null" || s.toLowerCase() === "undefined" || s === "[object Object]") return "";
    return s;
  };

  const loanProductId = getNumber(
    item?.loanProductId ?? item?.loanProductID ?? item?.LOAN_PRODUCT_ID ?? item?.productId ?? item?.id ?? item?.ID
  );

  const lenderId = getNumber(item?.lenderId ?? item?.lenderID ?? item?.LENDER_ID);

  const formatInterestRateStr = (val: string): string => {
    const numericPart = val.replace(/%/g, "").trim();
    if (numericPart === "") return "";
    const num = parseFloat(numericPart);
    if (isNaN(num)) return val;
    return `${num.toFixed(2)}%`;
  };

  let interestFromStr = "";
  const rawInterestFrom = item?.InterestFrom ?? item?.interestFrom ?? item?.INTEREST_FROM;
  const cleanInterestFrom = getCleanString(rawInterestFrom);
  if (cleanInterestFrom !== "") {
    interestFromStr = formatInterestRateStr(cleanInterestFrom);
  } else {
    try {
      const parsed = JSON.parse(getString(item?.loanDetailsJson ?? item?.LOAN_DETAILS_JSON));
      const config = parsed?.productTypeConfig;
      if (item.productType === "Property" && config?.property) {
        const bands = config.property.lvrInterestRates || [];
        const froms: number[] = [];
        bands.forEach((b: any) => {
          ["residential1stFrom", "residential2ndFrom", "commercialFrom", "farmRuralFrom"].forEach(k => {
            const v = parseFloat(b[k]);
            if (!isNaN(v) && v > 0) froms.push(v);
          });
        });
        if (froms.length > 0) interestFromStr = `${Math.min(...froms).toFixed(2)}%`;
      } else if (item.productType === "Asset - Secured" && config?.assetSecured) {
        const rates = config.assetSecured.interestRates || [];
        if (rates[0] && rates[0].from) {
          interestFromStr = `${parseFloat(rates[0].from).toFixed(2)}%`;
        }
      } else if (item.productType === "Asset - Unsecured" && config?.assetUnsecured) {
        const rates = config.assetUnsecured.interestRates || [];
        if (rates[0] && rates[0].from) {
          interestFromStr = `${parseFloat(rates[0].from).toFixed(2)}%`;
        }
      }
    } catch {}
  }

  let interestToStr = "";
  const rawInterestTo = item?.InterestTo ?? item?.interestTo ?? item?.INTEREST_TO;
  const cleanInterestTo = getCleanString(rawInterestTo);
  if (cleanInterestTo !== "") {
    interestToStr = formatInterestRateStr(cleanInterestTo);
  } else {
    try {
      const parsed = JSON.parse(getString(item?.loanDetailsJson ?? item?.LOAN_DETAILS_JSON));
      const config = parsed?.productTypeConfig;
      if (item.productType === "Property" && config?.property) {
        const bands = config.property.lvrInterestRates || [];
        const tos: number[] = [];
        bands.forEach((b: any) => {
          ["residential1stTo", "residential2ndTo", "commercialTo", "farmRuralTo"].forEach(k => {
            const v = parseFloat(b[k]);
            if (!isNaN(v) && v > 0) tos.push(v);
          });
        });
        if (tos.length > 0) interestToStr = `${Math.max(...tos).toFixed(2)}%`;
      } else if (item.productType === "Asset - Secured" && config?.assetSecured) {
        const rates = config.assetSecured.interestRates || [];
        if (rates[0] && rates[0].to) {
          interestToStr = `${parseFloat(rates[0].to).toFixed(2)}%`;
        }
      } else if (item.productType === "Asset - Unsecured" && config?.assetUnsecured) {
        const rates = config.assetUnsecured.interestRates || [];
        if (rates[0] && rates[0].to) {
          interestToStr = `${parseFloat(rates[0].to).toFixed(2)}%`;
        }
      }
    } catch {}
  }

  let loanAmountMinStr = "";
  const rawLoanAmountMin = item?.LoanAmountMin ?? item?.loanAmountMin ?? item?.LOAN_AMOUNT_MIN;
  const cleanLoanAmountMin = getCleanString(rawLoanAmountMin);
  if (cleanLoanAmountMin !== "") {
    loanAmountMinStr = cleanLoanAmountMin.startsWith("$") ? cleanLoanAmountMin : `$${cleanLoanAmountMin}`;
  } else {
    try {
      const parsed = JSON.parse(getString(item?.loanDetailsJson ?? item?.LOAN_DETAILS_JSON));
      const config = parsed?.productTypeConfig;
      let minAmt = "";
      if (item.productType === "Property" && config?.property) {
        minAmt = config.property.loanAmountMin;
      } else if (item.productType === "Asset - Secured" && config?.assetSecured) {
        minAmt = config.assetSecured.loanAmountMin;
      } else if (item.productType === "Asset - Unsecured" && config?.assetUnsecured) {
        minAmt = config.assetUnsecured.loanAmountMin;
      }
      if (minAmt) {
        loanAmountMinStr = minAmt.startsWith("$") ? minAmt : `$${minAmt}`;
      }
    } catch {}
  }

  return {
    loanProductId,
    lenderId,
    lenderName: getString(item?.lenderName ?? item?.LENDER_NAME).trim(),
    productName: getString(item?.productName ?? item?.PRODUCT_NAME).trim(),
    productType: getString(item?.productType ?? item?.PRODUCT_TYPE).trim(),
    productRank: getNumber(item?.productRank ?? item?.PRODUCT_RANK),
    loanDetailsJson: getString(item?.loanDetailsJson ?? item?.LOAN_DETAILS_JSON),
    isDeleted: Boolean(item?.isDeleted ?? item?.IS_DELETED),
    InterestFrom: interestFromStr || "0",
    InterestTo: interestToStr || "0",
    LoanAmountMin: loanAmountMinStr || "0",
  };
};

export const validateLoanProductForm = (form: LoanProductForm) => {
  const errors: Partial<Record<keyof LoanProductForm, string>> = {};

  if (!Number(form.lenderId)) {
    errors.lenderId = "Please select a lender.";
  }

  const productName = form.productName.trim();
  if (!productName) {
    errors.productName = "Product Name is required.";
  } else if (productName.length > 100) {
    errors.productName = "Product Name cannot exceed 100 characters.";
  }

  if (!form.productType.trim()) {
    errors.productType = "Product Type is required.";
  }


  const details = form.loanDetailsJson.trim();
  if (!details) {
    errors.loanDetailsJson = "Loan Details are required.";
  } else {
    try {
      JSON.parse(details);
    } catch {
      errors.loanDetailsJson = "Loan Details JSON is invalid.";
    }
  }

  return errors;
};

export const buildLoanProductPayload = (form: LoanProductForm, loanProductId?: number) => {
  const payload: any = {
    lenderId: Number(form.lenderId),
    productName: form.productName.trim(),
    productType: form.productType.trim(),
    productRank: 0,
    loanDetailsJson: form.loanDetailsJson.trim(),
  };

  if (loanProductId) {
    payload.loanProductId = loanProductId;
  }

  return payload;
};





